import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext'; // Aggiunto useAuth
import { api } from '../services/api';
import {
  BookOpenIcon,
  ChartBarIcon,
  ClockIcon,
  TrophyIcon,
  ArrowRightIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';

export const Home: React.FC = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth(); // Aggiunto per debug utente

  // Debug logging per l'utente loggato
  React.useEffect(() => {
    console.log('👤 [Home] Utente loggato:', user);
    const token = localStorage.getItem('quiz_token');
    console.log('🔑 [Home] Token utilizzato:', token ? `${token.substring(0, 20)}...` : 'Nessun token');
    
    // Debug per verificare se l'utente ha completato quiz
    if (user) {
      console.log('📧 [Home] Email utente:', user.email);
      console.log('🆔 [Home] ID utente:', user.id);
      
      // Confronta con l'utente che ha completato i quiz
      if (user.email === 'a.l@d-serviceitalia.it') {
        console.log('✅ [Home] Questo è l\'utente che ha completato i quiz!');
      } else {
        console.log('❌ [Home] Questo NON è l\'utente che ha completato i quiz');
        console.log('🔍 [Home] Utente attuale:', user.email);
        console.log('🔍 [Home] Utente con quiz completati: a.l@d-serviceitalia.it');
      }
    }
  }, [user]);

  // Query per ottenere le statistiche personali dell'utente
  const { data: personalStats, isLoading: isLoadingStats, error: statsError, refetch: refetchStats } = useQuery({
    queryKey: ['user-personal-stats'],
    queryFn: async () => {
      console.log('🔍🔍🔍 [Home] Chiamando getPersonalStats...');
      const token = localStorage.getItem('quiz_token');
      console.log('🔑🔑🔑 [Home] Token utilizzato:', token ? `${token.substring(0, 20)}...` : 'Nessun token');
      const result = await api.getPersonalStats();
      console.log('📊📊📊 [Home] Risposta getPersonalStats:', result);
      return result;
    },
    staleTime: 0, // Disabilita cache per aggiornamenti immediati
    refetchOnWindowFocus: true, // Aggiorna quando la finestra torna in focus
    refetchOnMount: true, // Aggiorna quando il componente si monta
    retry: (failureCount, error: any) => {
      // Non riprovare se è un errore di autenticazione
      if (error?.response?.status === 401) {
        return false;
      }
      return failureCount < 2;
    },
  });

  // Query per ottenere il numero di materie disponibili
  const { data: subjects, isLoading: isLoadingSubjects } = useQuery({
    queryKey: ['subjects'],
    queryFn: api.getSubjects,
    staleTime: 10 * 60 * 1000, // 10 minuti
  });

  // Debug logging per le statistiche
  React.useEffect(() => {
    if (statsError) {
      console.error('❌ [Home] Errore caricamento statistiche:', statsError);
    }
    if (personalStats) {
      console.log('✅ [Home] Statistiche caricate:', personalStats);
      console.log('📊 [Home] Dettagli statistiche:', {
        completedSessions: personalStats.completedSessions,
        totalTimeSpent: personalStats.totalTimeSpent,
        averageAccuracy: personalStats.averageAccuracy,
        totalQuestionsAnswered: personalStats.totalQuestionsAnswered,
        totalCorrectAnswers: personalStats.totalCorrectAnswers
      });
      
      // Debug per verificare se l'utente è quello giusto
      if (user) {
        console.log('🔍 [Home] VERIFICA UTENTE:');
        console.log('📧 [Home] Email utente loggato:', user.email);
        console.log('🆔 [Home] ID utente loggato:', user.id);
        console.log('📊 [Home] Quiz completati:', personalStats.completedSessions);
        
        if (user.email === 'a.l@d-serviceitalia.it') {
          console.log('✅ [Home] Questo è l\'utente che ha completato i quiz!');
          console.log('❌ [Home] MA le statistiche mostrano 0 - PROBLEMA!');
        } else {
          console.log('❌ [Home] Questo NON è l\'utente che ha completato i quiz');
          console.log('🔍 [Home] Utente attuale:', user.email);
          console.log('🔍 [Home] Utente con quiz completati: a.l@d-serviceitalia.it');
        }
      }
    }
    console.log('🔍 [Home] Stato caricamento:', { isLoadingStats, personalStats, subjects });
  }, [personalStats, statsError, isLoadingStats, subjects, user]);

  // Invalida la cache delle statistiche quando il componente si monta
  React.useEffect(() => {
    console.log('🔄 [Home] Invalidando cache statistiche...');
    queryClient.invalidateQueries({ queryKey: ['user-personal-stats'] });
  }, [queryClient]);

  // Aggiorna le statistiche quando l'utente torna alla dashboard
  React.useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        console.log('🔄 [Home] Finestra tornata in focus - aggiornando statistiche...');
        refetchStats();
      }
    };

    const handleFocus = () => {
      console.log('🔄 [Home] Finestra in focus - aggiornando statistiche...');
      refetchStats();
    };

    // Listener per quando la finestra torna visibile
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [refetchStats]);

  // Funzione per formattare il tempo in ore e minuti
  const formatTime = (milliseconds: number): string => {
    console.log('⏰ [Home] formatTime chiamata con:', milliseconds);
    
    if (!milliseconds || milliseconds === 0) return '0h 0m';
    
    const totalSeconds = Math.floor(milliseconds / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    
    console.log('⏰ [Home] Calcolo tempo:', { milliseconds, totalSeconds, hours, minutes, seconds });
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  };

  // Funzione per formattare la precisione
  const formatAccuracy = (accuracy: number): string => {
    if (!accuracy || accuracy === 0) return '0%';
    return `${Math.round(accuracy)}%`;
  };

  // Statistiche dinamiche basate sui dati reali
  const stats = [
    {
      name: 'Materie Disponibili',
      value: subjects ? `${subjects.length}+` : '...',
      icon: BookOpenIcon,
      color: 'text-primary-600',
      bgColor: 'bg-primary-100',
      isLoading: isLoadingSubjects,
    },
    {
      name: 'Quiz Completati',
      value: personalStats ? personalStats.completedSessions.toString() : '0',
      icon: TrophyIcon,
      color: 'text-success-600',
      bgColor: 'bg-success-100',
      isLoading: isLoadingStats,
    },
    {
      name: 'Tempo di Studio',
      value: personalStats ? formatTime(personalStats.totalTimeSpent) : '0h 0m',
      icon: ClockIcon,
      color: 'text-accent-600',
      bgColor: 'bg-accent-100',
      isLoading: isLoadingStats,
    },
    {
      name: 'Precisione Media',
      value: personalStats ? formatAccuracy(personalStats.averageAccuracy) : '0%',
      icon: ChartBarIcon,
      color: 'text-danger-600',
      bgColor: 'bg-danger-100',
      isLoading: isLoadingStats,
    },
  ];

  // Debug per vedere i valori finali delle statistiche
        console.log('📊 [Home] Valori statistiche:', {
          'Materie': subjects ? `${subjects.length}+` : '...',
          'Quiz Completati': personalStats ? personalStats.completedSessions.toString() : '0',
          'Tempo Studio': personalStats ? formatTime(personalStats.totalTimeSpent) : '0h 0m',
          'Precisione': personalStats ? formatAccuracy(personalStats.averageAccuracy) : '0%',
        });
        
        // Debug per verificare se l'utente è quello giusto
        if (user) {
          console.log('🔍 [Home] VERIFICA UTENTE:');
          console.log('📧 [Home] Email utente loggato:', user.email);
          console.log('🆔 [Home] ID utente loggato:', user.id);
          console.log('📊 [Home] Quiz completati:', personalStats ? personalStats.completedSessions : 'N/A');
          
          if (user.email === 'a.l@d-serviceitalia.it') {
            console.log('✅ [Home] Questo è l\'utente che ha completato i quiz!');
            console.log('❌ [Home] MA le statistiche mostrano 0 - PROBLEMA!');
          } else {
            console.log('❌ [Home] Questo NON è l\'utente che ha completato i quiz');
            console.log('🔍 [Home] Utente attuale:', user.email);
            console.log('🔍 [Home] Utente con quiz completati: a.l@d-serviceitalia.it');
          }
        }

  return (
    <div className="min-h-full space-y-4 sm:space-y-6 lg:space-y-8">
      {/* Header con pulsante refresh */}
      <div className="flex justify-between items-center">
        <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">
          Dashboard
        </h1>
        <div className="flex space-x-2">
          <button
            onClick={() => {
              console.log('🔄 [Home] Refresh manuale statistiche...');
              refetchStats();
            }}
            disabled={isLoadingStats}
            className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ArrowPathIcon className={`h-4 w-4 ${isLoadingStats ? 'animate-spin' : ''}`} />
            <span>{isLoadingStats ? 'Aggiornamento...' : 'Aggiorna'}</span>
          </button>
          
          <button
            onClick={() => {
              console.log('🗑️ [Home] Pulisci cache e ricarica...');
              queryClient.clear();
              window.location.reload();
            }}
            className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-red-700 bg-red-50 border border-red-300 rounded-md hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
          >
            <span>Reset Cache</span>
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 lg:gap-4">
        {stats.map((stat) => {
          const Icon = stat.icon;
          return (
            <div key={stat.name} className="card text-center p-3 sm:p-4">
              <div className={`w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 ${stat.bgColor} rounded-lg flex items-center justify-center mx-auto mb-2 sm:mb-3`}>
                <Icon className={`h-4 w-4 sm:h-5 sm:w-5 lg:h-6 lg:w-6 ${stat.color}`} />
              </div>
              <div className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">
                {stat.isLoading ? (
                  <div className="animate-pulse bg-gray-200 h-6 sm:h-7 lg:h-8 w-12 sm:w-14 lg:w-16 rounded mx-auto"></div>
                ) : (
                  stat.value
                )}
              </div>
              <p className="text-xs sm:text-sm text-gray-600 leading-tight">{stat.name}</p>
            </div>
          );
        })}
      </div>

      {/* Error Message */}
      {statsError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 sm:p-4">
          <p className="text-red-800 text-xs sm:text-sm">
            Errore nel caricamento delle statistiche. I dati potrebbero non essere aggiornati.
          </p>
        </div>
      )}

      {/* Quick Actions */}
      <div className="flex justify-center">
        {/* Start Quiz Card */}
        <div className="card-hover max-w-md w-full p-3 sm:p-4">
          <div className="flex flex-col sm:flex-row items-center space-y-3 sm:space-y-0 sm:space-x-4">
            <div className="flex-1 text-center sm:text-left">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900">Inizia un Quiz</h3>
              <p className="text-sm sm:text-base text-gray-600">Scegli una materia e inizia subito a studiare</p>
            </div>
            <Link
              to="/subjects"
              className="btn-primary flex items-center space-x-2 text-sm sm:text-base px-3 sm:px-4 py-2 sm:py-3"
            >
              <span>Quiz</span>
              <ArrowRightIcon className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};
